import React, { Component } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import moment from "moment";
import { toast } from "react-toastify";
import instance from "../../axios";
import Loader from "../../Layout/Loader";
import Button from "../common/Button";
import InspectionVehicleForm from "./InspectionVehicleForm";

class VehicleAssessmentPage extends Component {
  state = {
    vehicle: this.props.location?.state?.vehicle || null,
    vehicleLoading: !this.props.location?.state?.vehicle,
    vehicleError: "",
    assessmentData: [],
    assessmentLoading: false,
    assessmentError: "",
    view: "list",
    formMode: "create",
    assessmentId: null,
  };

  componentDidMount() {
    this.ensureVehicle().then(() => this.fetchAssessments());
  }

  ensureVehicle = async () => {
    if (this.state.vehicle) {
      this.setState({ vehicleLoading: false });
      return this.state.vehicle;
    }

    const id = this.props.params?.id;
    const loginUser = this.props.logInUser;
    if (!id) {
      this.setState({
        vehicleLoading: false,
        vehicleError: "Vehicle not found",
      });
      return null;
    }

    this.setState({ vehicleLoading: true, vehicleError: "" });
    try {
      const response = await instance({
        url: `/api/vehicles/get?search=${encodeURIComponent(id)}&all=true&userId=${loginUser?.id || ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const rows = response?.data || [];
      const vehicle =
        rows.find((v) => String(v.id) === String(id) || v.vin === id) ||
        rows[0] ||
        null;
      if (!vehicle) {
        this.setState({
          vehicleLoading: false,
          vehicleError: "Vehicle not found",
        });
        return null;
      }
      this.setState({ vehicle, vehicleLoading: false });
      return vehicle;
    } catch (error) {
      console.error("Error fetching vehicle:", error);
      this.setState({
        vehicleLoading: false,
        vehicleError: "Unable to load vehicle details",
      });
      return null;
    }
  };

  resolveVin = (vehicle = this.state.vehicle) =>
    vehicle?.vin || vehicle?.attributes?.vin || "";

  fetchAssessments = async () => {
    const vin = this.resolveVin();
    if (!vin) {
      this.setState({
        assessmentLoading: false,
        assessmentError: "VIN not found for this vehicle",
        assessmentData: [],
      });
      return;
    }

    this.setState({
      assessmentLoading: true,
      assessmentError: "",
      assessmentData: [],
    });

    try {
      const response = await instance({
        url: `/api/vehicleassessment/get?vin=${encodeURIComponent(vin)}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const rows = response?.data || [];
      this.setState({
        assessmentData: rows,
        assessmentLoading: false,
        assessmentError:
          response?.total > 0 || rows.length
            ? ""
            : "No assessment found for this VIN",
      });
    } catch (error) {
      console.error("Error fetching vehicle assessment:", error);
      this.setState({
        assessmentLoading: false,
        assessmentError: "Unable to load vehicle assessment",
        assessmentData: [],
      });
    }
  };

  formatBool = (value) => {
    if (value === true || value === "true" || value === 1 || value === "1") {
      return "Yes";
    }
    if (value === false || value === "false" || value === 0 || value === "0") {
      return "No";
    }
    return "-";
  };

  openCreate = () => {
    this.setState({
      view: "form",
      formMode: "create",
      assessmentId: null,
    });
  };

  openView = (row) => {
    if (!row?.id) {
      toast.error("Assessment id not found");
      return;
    }
    this.setState({
      view: "form",
      formMode: "view",
      assessmentId: row.id,
    });
  };

  backToList = (refresh = false) => {
    this.setState(
      {
        view: "list",
        formMode: "create",
        assessmentId: null,
      },
      () => {
        if (refresh) this.fetchAssessments();
      },
    );
  };

  goBack = () => {
    if (this.state.view === "form") {
      this.backToList(true);
      return;
    }
    if (this.props.navigate) {
      this.props.navigate("/vehicle");
    }
  };

  deleteAssessment = async (row) => {
    if (!row?.id) {
      toast.error("Assessment id not found");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this vehicle assessment?",
    );
    if (!confirmed) return;

    this.setState({ assessmentLoading: true });
    try {
      await instance({
        url: `/api/vehicleassessment/${row.id}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const nextRows = (this.state.assessmentData || []).filter(
        (item) => item.id !== row.id,
      );
      this.setState({
        assessmentData: nextRows,
        assessmentLoading: false,
        assessmentError: nextRows.length
          ? ""
          : "No assessment found for this VIN",
      });
      toast.success("Assessment deleted");
    } catch (error) {
      console.error("Error deleting vehicle assessment:", error);
      this.setState({ assessmentLoading: false });
      toast.error("Unable to delete vehicle assessment. Please try again.");
    }
  };

  renderForm = () => {
    const { vehicle, formMode, assessmentId } = this.state;
    return (
      <InspectionVehicleForm
        vehicle={vehicle}
        mode={formMode}
        assessmentId={assessmentId}
        onClose={() => this.backToList(true)}
      />
    );
  };

  renderList = () => {
    const { translate, logInUser, themecolors } = this.props;
    const {
      vehicle,
      assessmentData,
      assessmentLoading,
      assessmentError,
    } = this.state;
    const t = (key, fallback) => translate?.(key) || fallback;
    const isSuperAdmin = logInUser?.userType === -1;
    const headerBg = themecolors?.["500"] || "#1976d2";
    const headerText = themecolors?.themeInverse || "#fff";

    const headCellSx = {
      whiteSpace: "nowrap",
      backgroundColor: headerBg,
      color: headerText,
      fontWeight: 600,
      fontSize: "12px",
      py: 1,
      px: 1.25,
    };

    const bodyCellSx = {
      py: 1,
      px: 1.25,
      fontSize: "12px",
      whiteSpace: "nowrap",
      borderBottom: "1px solid #eee",
    };

    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={this.goBack} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
                {t("vehicleAssessment", "Vehicle Assessment")}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>
                {[
                  vehicle?.vehicleLicensePlate,
                  vehicle?.vin,
                  vehicle?.attributes?.contractNumber,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "12px", color: "#666" }}>
              {assessmentLoading
                ? t("loadingAssessments", "Loading assessments…")
                : `${assessmentData?.length || 0} ${t("records", "record(s)")}`}
            </Typography>
            <Button onClick={this.openCreate}>{t("create", "Create")}</Button>
          </Box>
        </Box>

        {assessmentLoading ? (
          <Box sx={{ py: 4 }}>
            <Loader />
          </Box>
        ) : assessmentError ? (
          <Typography sx={{ textAlign: "center", py: 6, color: "#666", fontSize: 13 }}>
            {assessmentError}
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: "calc(100vh - 220px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "VIN",
                    "Plate",
                    "Contract #",
                    "Current Mileage",
                    "Last Oil Change",
                    "Next Oil Change",
                    "Tyre",
                    "Battery",
                    "DTC Engine",
                    "DTC Emission",
                    "DTC Brakes",
                    "Action",
                  ].map((label) => (
                    <TableCell
                      key={label}
                      align={label === "Action" ? "center" : "left"}
                      sx={headCellSx}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {assessmentData.map((row, index) => (
                  <TableRow key={row.id || index} hover>
                    <TableCell sx={bodyCellSx}>{row.vin || "-"}</TableCell>
                    <TableCell sx={bodyCellSx}>{row.plate || "-"}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      {row.contractNumber || "-"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {row.currentMileage != null
                        ? `${row.currentMileage} km`
                        : "-"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {row.lastOilChangeMileage != null
                        ? `${row.lastOilChangeMileage} km`
                        : "-"}
                      {row.lastOilChangeDate
                        ? ` / ${moment(row.lastOilChangeDate).format("DD-MM-YYYY")}`
                        : ""}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {row.nextOilChangeMileage != null
                        ? `${row.nextOilChangeMileage} km`
                        : "-"}
                      {row.nextOilChangeDate
                        ? ` / ${moment(row.nextOilChangeDate).format("DD-MM-YYYY")}`
                        : ""}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {[row.tyreBrand, row.tyreSize].filter(Boolean).join(" ") ||
                        "-"}
                      {row.tyreExpDate
                        ? ` (Exp ${moment(row.tyreExpDate).format("DD-MM-YYYY")})`
                        : ""}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {[
                        row.batteryBrand,
                        row.batteryAmp != null ? `${row.batteryAmp} Ah` : null,
                      ]
                        .filter(Boolean)
                        .join(" ") || "-"}
                      {row.batteryExpDate
                        ? ` (Exp ${moment(row.batteryExpDate).format("DD-MM-YYYY")})`
                        : ""}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {this.formatBool(row.dtcEngine)}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {this.formatBool(row.dtcEmission)}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {this.formatBool(row.dtcBrakes)}
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title={t("view", "View")}>
                          <IconButton
                            size="small"
                            onClick={() => this.openView(row)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isSuperAdmin ? (
                          <Tooltip title={t("delete", "Delete")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => this.deleteAssessment(row)}
                              disabled={assessmentLoading || !row?.id}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    );
  };

  render() {
    const { translate } = this.props;
    const { vehicle, vehicleLoading, vehicleError, view } = this.state;
    const t = (key, fallback) => translate?.(key) || fallback;

    if (vehicleLoading) {
      return (
        <Box sx={{ p: 3 }}>
          <Loader />
        </Box>
      );
    }

    if (vehicleError || !vehicle) {
      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <IconButton
              size="small"
              onClick={() => this.props.navigate?.("/vehicle")}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography>{t("back", "Back")}</Typography>
          </Box>
          <Typography color="text.secondary">
            {vehicleError || "Vehicle not found"}
          </Typography>
        </Box>
      );
    }

    if (view === "form") {
      return this.renderForm();
    }

    return <Box sx={{ p: { xs: 2, md: 3 } }}>{this.renderList()}</Box>;
  }
}

export default VehicleAssessmentPage;
